import React, { useEffect, useState } from "react";
import { Modal, Input, Button, Switch, DatePicker } from "antd";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import { t } from "i18next";
import { toast } from "react-toastify";
import deleteicon from "../assets/Order/Delete.svg";

const ExpiryModal = ({
  visible,
  onClose,
  onSave,
  productIndex,
  initialData,
  purchaseQty,
  freeQty,
  initialAlertDays,
  initialExpiryAlert,
}) => {
  const [expiryData, setExpiryData] = useState([
    {
      batchNumber: "",
      qty: "",
      manufactureDate: null,
      shelfLife: "",
      expiryDate: null,
    },
  ]);

  const [expiryAlert, setExpiryAlert] = useState(false);
  const [alertDays, setAlertDays] = useState("");
  const [totalQty, setTotalQty] = useState(0);

  useEffect(() => {
    if (visible) {
      if (initialData && initialData.length > 0) {
        setExpiryData(initialData);
        setExpiryAlert(initialExpiryAlert);
        setAlertDays(initialAlertDays);
      } else {
        setExpiryData([
          {
            batchNumber: "",
            qty: "",
            manufactureDate: null,
            shelfLife: "",
            expiryDate: null,
          },
        ]);
        setExpiryAlert(false);
        setAlertDays("");
      }

      setTotalQty(0);
    }
  }, [visible, initialData]);

  const handleAddRow = () => {
    if (expiryData.length < 10) {
      setExpiryData([
        ...expiryData,
        {
          batchNumber: "",
          qty: "",
          manufactureDate: null,
          shelfLife: "",
          expiryDate: null,
        },
      ]);
    } else {
      toast.error("You can only add up to 10 rows.");
    }
  };

  const handleChange = (index, key, value) => {
    const newData = [...expiryData];

    if (key === "qty" || key === "shelfLife") {
      let numericValue = parseInt(value, 10) || 0;

      if (numericValue < 0) numericValue = 0;

      newData[index][key] = numericValue;

      if (key === "qty") {
        const newTotalQty = newData.reduce(
          (acc, item) => acc + (item.qty || 0),
          0
        );

        setTotalQty(newTotalQty);
      }
    } else {
      newData[index][key] = value;
    }

    setExpiryData(newData);
  };

  const calculateShelfLife = (manufactureDate, expiryDate) => {
    if (!manufactureDate || !expiryDate) return "";

    const mfg = moment(
      manufactureDate?.toDate
        ? manufactureDate.toDate()
        : manufactureDate
    );

    const exp = moment(
      expiryDate?.toDate
        ? expiryDate.toDate()
        : expiryDate
    );

    const diff = exp
      .startOf("day")
      .diff(mfg.startOf("day"), "days");

    return diff >= 0 ? diff : 0;
  };

  const handleManufactureDateChange = (index, date) => {
    const newData = [...expiryData];
    const currentExpiry = newData[index].expiryDate;

    if (
      date &&
      currentExpiry &&
      moment(date).isAfter(currentExpiry, "day")
    ) {
      toast.error(
        "Manufacture date cannot be greater than expiry date."
      );
      return;
    }

    newData[index].manufactureDate = date;

    if (date && currentExpiry) {
      newData[index].shelfLife = calculateShelfLife(
        date,
        currentExpiry
      );
    }

    setExpiryData(newData);
  };

  const handleDateChange = (index, date) => {
    const newData = [...expiryData];
    const currentMfg = newData[index].manufactureDate;

    if (
      date &&
      currentMfg &&
      moment(date).isBefore(currentMfg, "day")
    ) {
      toast.error(
        "Expiry date cannot be less than manufacture date."
      );
      return;
    }

    newData[index].expiryDate = date;

    if (date && currentMfg) {
      newData[index].shelfLife = calculateShelfLife(
        currentMfg,
        date
      );
    }

    setExpiryData(newData);
  };

  const handleSave = () => {
    if (totalQty > purchaseQty + freeQty) {
      toast.error(
        "Total quantity cannot exceed purchase quantity plus free quantity."
      );
      return;
    }

    for (let item of expiryData) {
      if (
        item.qty &&
        !item.manufactureDate &&
        !item.expiryDate
      ) {
        toast.error(
          "Please enter either Manufacture Date or Expiry Date."
        );
        return;
      }
    }

    onSave(
      expiryData,
      expiryAlert,
      alertDays,
      productIndex
    );

    onClose();
  };

  const handleClose = () => {
    const filteredData = expiryData.filter(
      (item) =>
        item.batchNumber ||
        item.qty ||
        item.manufactureDate ||
        item.shelfLife ||
        item.expiryDate
    );

    setExpiryData(filteredData);
    onClose();
  };

  const isButtonDisabled = expiryData.every((item) => {
    return (
      !item.batchNumber ||
      !item.qty ||
      !item.expiryDate
    );
  });

  const handleDeleteRow = (index) => {
    const updated = [...expiryData];
    updated.splice(index, 1);
    setExpiryData(updated);
  };

  return (
    <Modal
      title={t("Expiry")}
      visible={visible}
      onCancel={handleClose}
      footer={null}
      width="95%"
      style={{ maxWidth: "1000px" }}
    >
      <div className="expiry-page">
        {/* Desktop Header */}
        <div className="hidden md:flex mb-3 gap-2 flex-wrap">
          <div style={{ width: "90px" }}>
            {t("Quantity")}
            <span className="text-red">*</span>
          </div>

          <div style={{ width: "140px" }}>
            {t("Batch Number")}
            <span className="text-red">*</span>
          </div>

          <div style={{ width: "190px" }}>
            {t("Manufacture Date")}
          </div>

          <div style={{ width: "140px" }}>
            {t("Shelf Life (Days)")}
          </div>

          <div style={{ width: "190px" }}>
            {t("Expiry Date")}
            <span className="text-red">*</span>
          </div>
        </div>

        {expiryData.map((item, index) => (
          <div
            key={index}
            className="flex flex-col md:flex-row gap-3 md:gap-2 mb-4 items-start"
          >
            {/* Qty */}
            <div className="w-full md:w-auto">
              <div className="md:hidden mb-1 font-medium text-sm">
                {t("Quantity")}
              </div>

              <Input
                type="number"
                placeholder={t("Qty")}
                value={item.qty}
                onChange={(e) =>
                  handleChange(
                    index,
                    "qty",
                    e.target.value
                  )
                }
                onWheel={(e) => e.target.blur()}
                min={0}
                style={{
                  width: window.innerWidth < 768
                    ? "100%"
                    : "90px",
                }}
              />
            </div>

            {/* Batch Number */}
            <div className="w-full md:w-auto">
              <div className="md:hidden mb-1 font-medium text-sm">
                {t("Batch Number")}
              </div>

              <Input
                placeholder={t("Batch Number")}
                value={item.batchNumber}
                onChange={(e) =>
                  handleChange(
                    index,
                    "batchNumber",
                    e.target.value
                  )
                }
                style={{
                  width: window.innerWidth < 768
                    ? "100%"
                    : "140px",
                }}
                disabled={!item.qty}
              />
            </div>

            {/* Manufacture Date */}
            <div className="w-full md:w-auto">
              <div className="md:hidden mb-1 font-medium text-sm">
                {t("Manufacture Date")}
              </div>

              <DatePicker
                placeholder={t("Manufacture Date")}
                value={item.manufactureDate}
                onChange={(date) =>
                  handleManufactureDateChange(
                    index,
                    date
                  )
                }
                style={{
                  width: window.innerWidth < 768
                    ? "100%"
                    : "190px",
                }}
                disabled={!item.qty}
                disabledDate={(current) => {
                  if (!item.expiryDate) return false;

                  return current.isAfter(
                    item.expiryDate,
                    "day"
                  );
                }}
              />
            </div>

            {/* Shelf Life */}
            <div className="w-full md:w-auto">
              <div className="md:hidden mb-1 font-medium text-sm">
                {t("Shelf Life (Days)")}
              </div>

              <Input
                type="number"
                placeholder={t("Shelf Life (Days)")}
                value={item.shelfLife}
                onChange={(e) =>
                  handleChange(
                    index,
                    "shelfLife",
                    e.target.value
                  )
                }
                min={0}
                style={{
                  width: window.innerWidth < 768
                    ? "100%"
                    : "140px",
                }}
                disabled={
                  !item.manufactureDate ||
                  !item.qty ||
                  (item.manufactureDate &&
                    item.expiryDate)
                }
              />
            </div>

            {/* Expiry Date */}
            <div className="w-full md:w-auto">
              <div className="md:hidden mb-1 font-medium text-sm">
                {t("Expiry Date")}
              </div>

              <DatePicker
                placeholder={t("Expiry Date")}
                value={item.expiryDate}
                onChange={(date) =>
                  handleDateChange(index, date)
                }
                style={{
                  width: window.innerWidth < 768
                    ? "100%"
                    : "190px",
                }}
                disabled={!item.qty}
                disabledDate={(current) => {
                  if (!item.manufactureDate) return false;

                  return current.isBefore(
                    item.manufactureDate,
                    "day"
                  );
                }}
              />
            </div>

            {/* Delete */}
            {index !== 0 && (
              <img
                className="w-4 h-4 cursor-pointer self-end md:self-auto md:mt-2"
                src={deleteicon}
                alt="deleteicon"
                onClick={() =>
                  handleDeleteRow(index)
                }
              />
            )}
          </div>
        ))}

        <Button
          onClick={handleAddRow}
          type="dashed"
          className="w-full md:w-auto"
          style={{ marginBottom: "12px" }}
        >
          {t("Add")} +
        </Button>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSave}
            className="bg-primaryBlue w-full md:w-auto"
            type="primary"
            disabled={isButtonDisabled}
          >
            {t("save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExpiryModal;